import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Users, Clock, IndianRupee, CheckCircle2, XCircle, Calendar, User, ShieldAlert, Share2, Trophy, Star, Award } from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { MatchResultModal } from '@/components/MatchResultModal';

import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

function MatchDetail() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [user, setUser] = useState(null);
  const [myRsvp, setMyRsvp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [userRes, matchRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/matches/${matchId}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      setUser(userRes.data);
      setMatch(matchRes.data);
      
      const myRsvpData = matchRes.data.rsvps?.find(r => r.user_id === userRes.data.user_id);
      setMyRsvp(myRsvpData || null);
    } catch (error) {
      toast.error('Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!match) return;
    
    const shareUrl = `${window.location.origin}/match/${match.match_id}`;
    const matchDate = format(new Date(match.date), 'dd MMM');
    const matchType = match.match_type === 'saturday' ? 'Saturday' : 'Sunday';
    const shareText = `Join us for ${matchType} Cricket on ${matchDate}! RSVP now:`;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Weakenders ${matchType} Match`,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fallback to clipboard
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleRSVP = async (status) => {
    try {
      await axios.post(
        `${API_URL}/api/rsvps`,
        { match_id: matchId, status },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(`RSVP updated to ${status.toUpperCase()}`);
      fetchData();
    } catch (error) {
      toast.error('RSVP failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isSelected = match.selected_players?.includes(user?.user_id);
  const isWaitlisted = myRsvp?.status === 'in' && !isSelected;
  const rsvpIn = match.rsvps?.filter(r => r.status === 'in') || [];
  const rsvpOut = match.rsvps?.filter(r => r.status === 'out') || [];
  const selectedRsvps = rsvpIn.filter(r => match.selected_players?.includes(r.user_id));
  const waitlistRsvps = rsvpIn.filter(r => !match.selected_players?.includes(r.user_id));

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="icon"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-[#0F172A] uppercase">
                {match.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
              </h1>
              <p className="text-xs text-slate-600">
                {format(new Date(match.date), 'EEEE, dd MMM yyyy')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleShare}
            variant="ghost"
            size="icon"
            className="text-slate-600 hover:text-[#E63946] hover:bg-[#E63946]/10"
            data-testid="share-match-detail-btn"
          >
            <Share2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Match Info */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-slate-600">Players</p>
              <p className="text-2xl font-bold text-[#0F172A]">
                {match.selected_players?.length || 0}/{match.max_players}
              </p>
            </div>
            {match.cost && (
              <div>
                <p className="text-sm text-slate-600">Cost Per Player</p>
                <p className="text-2xl font-bold text-[#E63946]">₹{match.cost / match.selected_players?.length || 0}</p>
              </div>
            )}
          </div>

          {/* RSVP Toggle */}
          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-bold text-[#0F172A] text-lg">Your RSVP</p>
                <p className="text-sm text-slate-600">
                  {myRsvp?.status === 'in' ? (
                    isSelected ? 'You are SELECTED' : 'You are on WAITLIST'
                  ) : 'Not attending'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-semibold ${myRsvp?.status === 'out' ? 'text-[#E63946]' : 'text-slate-400'}`}>
                  OUT
                </span>
                <Switch
                  checked={myRsvp?.status === 'in'}
                  onCheckedChange={(checked) => handleRSVP(checked ? 'in' : 'out')}
                  className="data-[state=checked]:bg-green-600"
                  data-testid="rsvp-toggle"
                />
                <span className={`text-sm font-semibold ${myRsvp?.status === 'in' ? 'text-green-600' : 'text-slate-400'}`}>
                  IN
                </span>
              </div>
            </div>

            {isWaitlisted && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900">You're on the waitlist</p>
                  <p className="text-sm text-slate-700">You'll be auto-promoted if a spot opens up</p>
                </div>
              </div>
            )}

            {isSelected && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900">You're selected for this match!</p>
                  <p className="text-sm text-green-700">See you on the field</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Players */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Selected Players ({selectedRsvps.length})
          </h2>
          {selectedRsvps.length === 0 ? (
            <p className="text-slate-600 text-center py-4">No players selected yet</p>
          ) : (
            <div className="space-y-2">
              {selectedRsvps.map((rsvp, idx) => (
                <PlayerRow key={rsvp.rsvp_id} player={rsvp.user} number={idx + 1} />
              ))}
            </div>
          )}
        </div>

        {/* Waitlist */}
        {waitlistRsvps.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Waitlist ({waitlistRsvps.length})
            </h2>
            <div className="space-y-2">
              {waitlistRsvps.map((rsvp, idx) => (
                <PlayerRow key={rsvp.rsvp_id} player={rsvp.user} number={idx + 1} waitlist />
              ))}
            </div>
          </div>
        )}

        {/* All Votes Section */}
        <AllVotesSection rsvpIn={rsvpIn} rsvpOut={rsvpOut} />

        {/* Sunday Teams */}
        {match.match_type === 'sunday' && (match.team_a?.length > 0 || match.team_b?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TeamCard title="Team A (Bhandball)" players={match.team_a} rsvps={match.rsvps} />
            <TeamCard title="Team B (Kacche Khiladi)" players={match.team_b} rsvps={match.rsvps} />
          </div>
        )}

        {/* Match Result */}
        {match.result && (
          <MatchResultCard 
            match={match} 
            isAdmin={user?.is_admin}
            onEdit={() => setShowResultModal(true)}
          />
        )}

        {/* Admin: Record Result Button */}
        {user?.is_admin && !match.result && match.selected_players?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="text-center">
              <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-4">No match result recorded yet</p>
              <Button
                onClick={() => setShowResultModal(true)}
                className="bg-[#E63946] hover:bg-[#DC2626]"
                data-testid="record-result-btn"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Record Result
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Match Result Modal */}
      <MatchResultModal
        match={match}
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        onSave={fetchData}
      />

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-between items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <NavButton icon={<Calendar />} label="Matches" onClick={() => navigate('/dashboard')} />
        <NavButton icon={<User />} label="Profile" onClick={() => navigate('/profile')} />
        {user?.is_admin && (
          <NavButton icon={<ShieldAlert />} label="Admin" onClick={() => navigate('/admin')} />
        )}
      </div>
    </div>
  );
}

function AllVotesSection({ rsvpIn, rsvpOut }) {
  const [expanded, setExpanded] = useState(false);
  const totalVotes = rsvpIn.length + rsvpOut.length;

  if (totalVotes === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid="all-votes-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-slate-600" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-bold text-[#0F172A]">All Votes</h2>
            <p className="text-sm text-slate-600">
              <span className="text-green-600 font-semibold">{rsvpIn.length} IN</span>
              <span className="mx-2">•</span>
              <span className="text-red-500 font-semibold">{rsvpOut.length} OUT</span>
            </p>
          </div>
        </div>
        <div className={`transform transition-transform ${expanded ? 'rotate-180' : ''}`}>
          <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="border-t border-slate-200"
        >
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* IN Votes */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-bold text-green-700">Playing ({rsvpIn.length})</h3>
              </div>
              {rsvpIn.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No one has voted IN yet</p>
              ) : (
                <div className="space-y-2">
                  {rsvpIn.map((rsvp) => (
                    <VoteRow key={rsvp.rsvp_id} player={rsvp.user} status="in" />
                  ))}
                </div>
              )}
            </div>

            {/* OUT Votes */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-500" />
                </div>
                <h3 className="font-bold text-red-600">Not Playing ({rsvpOut.length})</h3>
              </div>
              {rsvpOut.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No one has voted OUT</p>
              ) : (
                <div className="space-y-2">
                  {rsvpOut.map((rsvp) => (
                    <VoteRow key={rsvp.rsvp_id} player={rsvp.user} status="out" />
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function VoteRow({ player, status }) {
  return (
    <div 
      className={`flex items-center gap-3 p-2 rounded-lg ${
        status === 'in' ? 'bg-green-50' : 'bg-red-50'
      }`}
      data-testid={`vote-${player?.user_id}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
        status === 'in' ? 'bg-green-500' : 'bg-red-400'
      }`}>
        {player?.name?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-[#0F172A] truncate">{player?.nickname || player?.name}</p>
        <p className="text-xs text-slate-500 truncate">{player?.cricket_role}</p>
      </div>
    </div>
  );
}

function NavButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
        active ? 'text-[#E63946]' : 'text-slate-600 hover:text-[#E63946]'
      }`}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      <div className="w-6 h-6">{icon}</div>
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}

function PlayerRow({ player, number, waitlist }) {
  return (
    <motion.div
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        waitlist ? 'bg-slate-50 border-slate-200' : 'bg-slate-50 border-slate-200'
      }`}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: number * 0.02 }}
      data-testid={`player-${player?.user_id}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
        waitlist ? 'bg-slate-200 text-slate-900' : 'bg-[#E63946] text-white'
      }`}>
        {number}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-[#0F172A]">{player?.name}</p>
        <p className="text-xs text-slate-600">{player?.cricket_role}</p>
      </div>
    </motion.div>
  );
}

function TeamCard({ title, players, rsvps }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border-2 border-[#E63946] p-5">
      <h3 className="text-lg font-bold text-[#0F172A] mb-4">{title}</h3>
      <div className="space-y-2">
        {players?.map((playerId, idx) => {
          const rsvp = rsvps?.find(r => r.user_id === playerId);
          return rsvp ? <PlayerRow key={playerId} player={rsvp.user} number={idx + 1} /> : null;
        })}
      </div>
    </div>
  );
}

function MatchResultCard({ match, isAdmin, onEdit }) {
  const result = match.result;
  const mom = result?.man_of_match_user;
  
  const getWinnerLabel = () => {
    if (result?.winner === 'team_a') {
      return match.match_type === 'sunday' ? 'Team A (Bhandball)' : 'Weakenders';
    }
    if (result?.winner === 'team_b') {
      return match.match_type === 'sunday' ? 'Team B (Kacche Khiladi)' : match.opponent || 'Opponent';
    }
    if (result?.winner === 'tie') return 'Match Tied';
    if (result?.winner === 'no_result') return 'No Result';
    return null;
  };

  const getTeamALabel = () => {
    return match.match_type === 'sunday' ? 'Bhandball' : 'Weakenders';
  };

  const getTeamBLabel = () => {
    return match.match_type === 'sunday' ? 'Kacche Khiladi' : match.opponent || 'Opponent';
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-[#0F172A] to-slate-800 rounded-xl shadow-lg p-6 text-white"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="match-result-card"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          Match Result
        </h2>
        {isAdmin && (
          <Button
            onClick={onEdit}
            variant="outline"
            size="sm"
            className="text-white border-white/30 hover:bg-white/10"
            data-testid="edit-result-btn"
          >
            Edit
          </Button>
        )}
      </div>

      {/* Scores */}
      {(result?.team_a_score || result?.team_b_score) && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-white/10 rounded-lg">
            <p className="text-sm text-slate-300 mb-1">{getTeamALabel()}</p>
            <p className="text-xl font-bold">{result?.team_a_score || '-'}</p>
          </div>
          <div className="text-center p-3 bg-white/10 rounded-lg">
            <p className="text-sm text-slate-300 mb-1">{getTeamBLabel()}</p>
            <p className="text-xl font-bold">{result?.team_b_score || '-'}</p>
          </div>
        </div>
      )}

      {/* Winner */}
      {result?.winner && (
        <div className="flex items-center justify-center gap-2 mb-4 py-3 bg-[#E63946]/20 rounded-lg">
          <Award className="w-5 h-5 text-[#E63946]" />
          <span className="font-bold text-lg">
            {result?.winner === 'tie' || result?.winner === 'no_result' 
              ? getWinnerLabel() 
              : `${getWinnerLabel()} Won!`}
          </span>
        </div>
      )}

      {/* Man of the Match */}
      {mom && (
        <div className="flex items-center gap-3 p-3 bg-yellow-500/20 rounded-lg mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
            <Star className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-yellow-300">Man of the Match</p>
            <p className="font-bold text-lg">{mom.nickname || mom.name}</p>
          </div>
        </div>
      )}

      {/* Highlights */}
      {result?.highlights && (
        <div className="text-sm text-slate-300 border-t border-white/10 pt-4 mt-2">
          <p className="text-xs text-slate-400 mb-1">Highlights</p>
          <p>{result.highlights}</p>
        </div>
      )}
    </motion.div>
  );
}

export default MatchDetail;